import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  User, HandHeart, Dumbbell, Salad, Leaf, Sparkles, Repeat, ShieldCheck, ArrowRight,
} from "lucide-react";
import { Reveal, ChapterHeading } from "./Reveal";

const ABOUT_IMG = "/photos/client-transformation.jpg";

const PILLARS = [
  { icon: User, label: "Personalized coaching" },
  { icon: HandHeart, label: "Accountability" },
  { icon: Dumbbell, label: "Strength & fitness" },
  { icon: Salad, label: "Nutrition guidance" },
  { icon: Leaf, label: "Healthy lifestyle habits" },
  { icon: Sparkles, label: "Building confidence" },
  { icon: Repeat, label: "Sustainable results" },
  { icon: ShieldCheck, label: "A supportive, judgment-free space" },
];

const About = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" ref={ref} className="py-24 sm:py-32 bg-mint" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <ChapterHeading number="01" label="Philosophy" title="Your Health. Your Goals. Your Journey." />
          <Reveal delay={0.1}>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-charcoal/70 max-w-2xl">
              Me 1st Fitness &amp; Coaching exists for one reason: to help you put yourself first —
              maybe for the first time in a long time. Through fitness, movement, nutrition and
              sustainable healthy habits, we help everyday people in Red Deer build bodies and
              routines that support the life they want to live.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-charcoal/70 max-w-2xl">
              This isn't a bootcamp and it isn't a quick fix. It's personal, encouraging coaching
              that meets you where you are — and walks with you as far as you want to go.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl">
            {PILLARS.map((p, i) => (
              <Reveal key={p.label} delay={0.05 * i} y={20}>
                <div className="flex items-center gap-3" data-testid={`about-pillar-${i}`}>
                  <span className="w-9 h-9 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                    <p.icon size={17} className="text-brand-dark" />
                  </span>
                  <span className="font-medium text-charcoal/80 text-sm sm:text-base">{p.label}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <button
              onClick={() => navigate("/coach")}
              data-testid="about-meet-coach-btn"
              className="group mt-10 rounded-full bg-charcoal text-sand font-bold px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2 hover:bg-brand hover:text-charcoal transition-colors duration-300"
            >
              MEET YOUR COACH
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-5 relative">
          <Reveal delay={0.15}>
            <div className="absolute -top-6 -right-6 w-full h-full rounded-3xl bg-sage/50" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.1)]">
              <motion.img
                src={ABOUT_IMG}
                alt="Real Me 1st Fitness client transformation — progress from July to October 2025"
                style={{ y: imgY }}
                className="w-full h-[26rem] sm:h-[32rem] object-cover scale-110"
                data-testid="about-image"
              />
              <span
                className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.2em] font-bold text-charcoal bg-brand rounded-full px-3 py-1.5"
                data-testid="about-image-caption"
              >
                Real client result
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
